import { useEffect, useState } from "react";

import { useAuthContext } from "../AuthContext";
import { hasPausedSiteContext } from "../../common/permissions";
import { siteBillingService } from "../../api/services/site-billing.service";
import type { SiteBillingMeStatus } from "../../api/types/site-billing";

const hasSiteOwnerCapability = (userInfo: ReturnType<typeof useAuthContext>["userInfo"]) => {
  return Boolean(userInfo?.capabilities?.includes("site_owner"));
};

const hasUnpaidBilling = (billingStatus: SiteBillingMeStatus | null) => {
  if (!billingStatus?.enabled) {
    return false;
  }

  const latestStatus = billingStatus.latest_invoice_status;
  const unpaidBalance = Number(billingStatus.unpaid_balance ?? 0);
  const latestBalanceDue = Number(billingStatus.latest_balance_due ?? 0);

  return (
    latestStatus === "unpaid" ||
    latestStatus === "partial_paid" ||
    unpaidBalance > 0 ||
    latestBalanceDue > 0
  );
};

const SitePauseBanner = () => {
  const { userInfo } = useAuthContext();
  const isPausedContext = hasPausedSiteContext(userInfo);
  const shouldLoadBillingStatus = !isPausedContext && hasSiteOwnerCapability(userInfo);
  const [billingStatus, setBillingStatus] = useState<SiteBillingMeStatus | null>(null);

  useEffect(() => {
    if (!shouldLoadBillingStatus) {
      setBillingStatus(null);
      return;
    }

    let isMounted = true;
    setBillingStatus(null);
    siteBillingService
      .getMyStatus()
      .then((response) => {
        if (isMounted) setBillingStatus(response.data);
      })
      .catch((error) => {
        console.warn("Failed to load site billing warning status", error);
        if (isMounted) setBillingStatus(null);
      });

    return () => {
      isMounted = false;
    };
  }, [shouldLoadBillingStatus, userInfo?.id, userInfo?.site_permission?.site]);

  if (isPausedContext) {
    return (
      <div
        className="alert alert-danger rounded-0 border-0 border-bottom border-danger-subtle mb-0 px-3 py-2"
        style={{ position: "sticky", top: 0, zIndex: 1030 }}
        role="status"
      >
        <strong>사이트 운영 일시정지</strong>
        <span className="ms-2">
          신규 발행, 상품 관리, 예약/티켓/쇼핑/쿠폰 운영 액션이 제한됩니다.
        </span>
      </div>
    );
  }

  if (!hasUnpaidBilling(billingStatus)) {
    return null;
  }

  return (
    <div
      className="alert alert-warning rounded-0 border-0 border-bottom border-warning-subtle mb-0 px-3 py-2"
      style={{ position: "sticky", top: 0, zIndex: 1030 }}
      role="status"
    >
      <strong>사이트 사용료 미납 안내</strong>
      <span className="ms-2">
        미납 사용료가 납부되지 않으면 사이트 이용이 일시정지될 수 있습니다.
      </span>
    </div>
  );
};

export default SitePauseBanner;
